<script setup>
import { computed, ref, onMounted, onUnmounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useCommunicatorStore } from '@/stores/communicator.js';
import LocalStorage from '@/controller/LocalStorage.js';
import { isAuthorized } from "@/utils/Authorization.js";
import { useI18n } from "vue-i18n";
import { storeToRefs } from "pinia";
import { useGlobalStore } from '@/stores/global.js';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import Notifications from '@/components/common/navbars/components/Notifications.vue';
import GlobalSearcher from "@/components/common/navbars/components/GlobalSearcher.vue";
import MazDropdown from 'maz-ui/components/MazDropdown';

const props = defineProps({
  animateToWorkfiles: Number
})
const isIveco = ref(LocalStorage.isIveco());
const router = useRouter();
const globalsStore = useGlobalStore();
const communicatorStore = useCommunicatorStore();
const { getLastBrandingChanges: lastBrandingChanges } = storeToRefs(communicatorStore);
const path = router.currentRoute.value;
const homeIcon = '/assets/home/home_yauty.svg';
const yautyLogo = '/assets/logos/yauty-logo.svg';
const orIcon = '/assets/home/reparacion.svg';
const carIcon = isIveco.value ? '/assets/home/truck.svg' : '/assets/home/car.svg';
const calendarIcon = '/assets/workfiles/0.svg';
const configIcon = '/assets/home/ajustes.svg';
const adminIcon = '/assets/svg/private.svg';
const userColor = ref(LocalStorage.getColor());
const userSecondaryColor = ref(LocalStorage.getColorSecondary());
const userTertiaryColor = ref(LocalStorage.getColorTertiary());
const selectedColor = ref(LocalStorage.getColorSelected());
const selectedColorHover = ref(LocalStorage.getColorSelectedHover());
const { t } = useI18n();
const isHome = computed(() => router.currentRoute.value.path === '/');
const isProfile = computed(() => router.currentRoute.value.path === '/user');
const sliderDisplay = computed(() => {
  if (isProfile.value || router.currentRoute.value.name === 'notifications' || router.currentRoute.value.name === 'invitation') {
    return {
      visibility: 'hidden',
    };
  } else {
    return {
      visibility: 'visible',
    }
  }
})

const isMenuOpen = ref(false);
const navbarTabletRef = ref(null);
const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value;
};
const handleClickOutside = (event) => {
  if (navbarTabletRef.value && !navbarTabletRef.value.contains(event.target)) {
    isMenuOpen.value = false;
  }
};
let yautyData = ref("");
const userNick = ref("");
const userName = ref("");
if (localStorage.getItem('yautyData')) {
  yautyData = ref(JSON.parse(localStorage.getItem('yautyData')));
  userNick.value = yautyData.value.name.charAt(0).toUpperCase();
  userName.value = yautyData.value.name.charAt(0).toUpperCase() + yautyData.value.name.slice(1);

}
const isWorkfileRoute = computed(() => {
  return router.currentRoute.value.path.startsWith('/workfile/');
});
const navbarStyle = {};
const menu = ref(null);
const slider = ref(null);
const currentSliderIndex = ref(0);
function animateSlider(index) {
  // El índice + 1 para saltarse el hSlider que es el primer hijo
  let item = menu.value?.children[index + 1];
  
  // Si el item es un div (por v-if), buscar el li dentro
  if (item && item.tagName === 'DIV') {
    item = item.querySelector('li');
  }
  
  if (!item) return;
  
  const itemWidth = item.offsetWidth;
  const newPosition = index * itemWidth;

  if (slider.value) {
    slider.value.style.transform = `translateX(${newPosition}px)`;
    slider.value.style.width = `${itemWidth}px`;
    currentSliderIndex.value = index;
  }
}

const isHidden = ref(false);
let lastScrollTop = 0;
const getScrollTop = (evt) => {
  if (evt && evt.target && typeof evt.target.scrollTop === 'number' && evt.target !== window) {
    return evt.target.scrollTop || 0;
  }
  return window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
};
const handleScroll = (event) => {
  const scrollTop = getScrollTop(event);
  const threshold = 2; // reduce jitter
  if (scrollTop > lastScrollTop + threshold) {
    isHidden.value = true;
  } else if (scrollTop < lastScrollTop - threshold) {
    isHidden.value = false;
  }
  lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
};

const brandingChanged = computed(() => lastBrandingChanges.value);
const viewChanged = computed(() => globalsStore.getCurrentView);

watch(brandingChanged, (value) => {
  if (value) {
    userColor.value = LocalStorage.getColor();
    userSecondaryColor.value = LocalStorage.getColorSecondary();
    userTertiaryColor.value = LocalStorage.getColorTertiary();
    selectedColor.value = LocalStorage.getColorSelected();
    selectedColorHover.value = LocalStorage.getColorSelectedHover();
  }
});
watch(viewChanged, (value) => {
  if (value) {
    switch (value) {
      case 'user':
        break;
      case 'home':
        animateSlider(0);
        break;
      case 'vehicles':
      case 'vehicle':
        animateSlider(1);
        break;
      case 'calendar':
        animateSlider(2);
        break;
      case 'workfiles':
      case 'workfile':
        animateSlider(3);
        break;
      case 'configuration':
      case 'configurationList':
        animateSlider(4);
        break;
      case 'ifs':
        animateSlider(5);
        break;
      case 'logout':
        break;
    }
  }
});
watch(() => props.animateToWorkfiles, (value) => {
  if (value) {
    animateSlider(3);
  }
});
watch(isHidden, (hidden) => {
  if (!hidden && currentSliderIndex.value !== null) {
    // Restaurar la posición del slider cuando la navbar se vuelve visible
    setTimeout(() => {
      animateSlider(currentSliderIndex.value);
    }, 50);
  }
});

onMounted(() => {
  if (['home', 'login'].includes(path.name)) {
    setTimeout(() => {
      animateSlider(0);
    }, 500);
  }
  window.addEventListener('scroll', handleScroll, { passive: true });

  const appContainer = document.getElementById('app');
  if (appContainer) {
    appContainer.addEventListener('scroll', handleScroll, { passive: true });
  }
});
onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
  const appContainer = document.getElementById('app');
  if (appContainer) {
    appContainer.removeEventListener('scroll', handleScroll);
  }
});

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});

var listItems = [];

onMounted(() => {
  const ua = navigator.userAgent.toLowerCase();
  const isSafari =
    ua.includes('safari')
  if (isSafari) {
    document.documentElement.classList.add('safari-browser');
  }
});

onUnmounted(() => {
  document.documentElement.classList.remove('safari-browser');
});
</script>

<template>
  <nav v-if="!$route.meta.hideNavbar" class="navbar" :style="navbarStyle"
  :class="[{ 'hidden': isHidden }, isHome ? 'navbar-home' : 'navbar-default', isWorkfileRoute ? 'workfile-nav' : 'navbar-default']"
  ref="navbarTabletRef">
  <div class="navbar__brand yauty-logo" @click="animateSlider(0)">
      <router-link to="/" class="nav-link" title="" data-bs-toggle="tooltip" data-bs-placement="right"
        data-bs-original-title="Home">
        <span>
          <img :style="yautyLogoStyle" :src="yautyLogo" style="width:22dvw;height:auto" />
        </span>
      </router-link>
    </div>
    <div class="menu nav-item" style="margin-left:7.5dvw; width:92.5dvw;">
      <div class="nav-item d-flex global-searcher-wrapper-item" style="flex-grow:1;">
        <GlobalSearcher />
      </div>
      <div class="nav-item d-flex notifications-item">
        <Notifications />
      </div>
      <div class="user-dropdown desktop" id="userDropdown">
        <MazDropdown trigger="click" size="xs" position="bottom right" no-chevron>
          <template #default>
            <div class="user-icon-wrapper">
              <div class="user-icon" :title="$t('user.user')">
                {{ userNick }}
              </div>
            </div>
          </template>
          <template #dropdown>
            <div class="user-dropdown-body">
              <div class="title">
                <div class="title-label">
                  {{ userName }}
                </div>
              </div>
              <hr class="undername-separator" />
              <div class="user-dropdown-actions">
                <router-link to="/user" class="btn-header-route" title="Ajustos de perfil">
                  <div class="show-all-wrapper">
                    <span>{{ $t("user.profile") }}</span>
                  </div>
                </router-link>
                <router-link to="/logout" class="btn-header-route" title="Tancar sessió">
                  <div class="show-all-wrapper">
                    <span>{{ $t("logout.logout") }}</span>
                  </div>
                </router-link>
              </div>
            </div>
          </template>
        </MazDropdown>
      </div>
    </div>
    <ul class="top-navbar-vertical-tablet" ref="menu">
      <div class="hSlider" ref="slider" :style="sliderDisplay"></div>
      <li class="nav-item" @click="animateSlider(0)">
        <router-link to="/" class="route-linker" :class="router.currentRoute.value.name === 'home' ? 'active' : ''">
          <div class="sidenav-item">
            <div class="sidenav-link">
              <div class="item-icon">
                <img class="navIcons" :src="homeIcon" style="width:3.5dvw;" />
              </div>
              <div class="menu-label">{{ $t('route.home') }}</div>
            </div>
          </div>
        </router-link>
      </li>
      <li class="nav-item" @click="animateSlider(1)">
        <router-link to="/vehicles" class="route-linker" :class="router.currentRoute.value.name === 'vehicles' ||
          router.currentRoute.value.name === 'vehicle' ||
          router.currentRoute.value.name === 'vehicle/add' ||
          router.currentRoute.value.name === 'createAppointment'
          ? 'active'
          : ''">
          <div class="sidenav-item">
            <div class="sidenav-link">
              <div class="item-icon">
                <img :src="carIcon" style="width:4.5dvw;" />
              </div>
              <div class="menu-label">{{ $t('route.vehicles') }}</div>
            </div>
          </div>
        </router-link>
      </li>
      <li class="nav-item" @click="animateSlider(2)">
        <router-link to="/calendar" class="route-linker" :class="router.currentRoute.value.name === 'calendar' ? 'active' : ''">
          <div class="sidenav-item">
            <div class="sidenav-link">
              <div class="item-icon">
                <img class="navIcons" :src="calendarIcon" style="width:3.7dvw;" />
              </div>
              <div class="menu-label">{{ $t('route.calendar') }}</div>
            </div>
          </div>
        </router-link>
      </li>
      <li class="nav-item" @click="animateSlider(3)">
        <router-link to="/workfiles" class="route-linker" :class="router.currentRoute.value.name === 'workfiles' ? 'active' : '' ||
          router.currentRoute.value.name === 'workfile' ? 'active' : ''
          ">
          <div class="sidenav-item">
            <div class="sidenav-link">
              <div class="item-icon">
                <img class="navIcons" :src="orIcon" style="width:4dvw;" />
              </div>
              <div class="menu-label">{{ $t('route.workfiles') }}</div>
            </div>
          </div>
        </router-link>
      </li>
      <div v-if="isAuthorized('organizationMenu')" data-bs-trigger="hover" :data-bs-content="t('route.configuration')"
        :ref="el => { listItems[3] = el }">
        <li class="nav-item" @click="animateSlider(4)">
          <router-link to="/configuration" class="route-linker" :class="router.currentRoute.value.name === 'configurationList' ||
            router.currentRoute.value.name === 'configuration' ||
            router.currentRoute.value.name === 'createInvitation' ? 'active' : ''
            ">
            <div class="sidenav-item">
              <div class="sidenav-link">
                <div class="item-icon">
                  <img class="navIcons" :src="configIcon" style="width:4dvw;" />
                </div>
                <div class="menu-label">{{ $t('route.configuration') }}</div>
              </div>
            </div>
          </router-link>
        </li>
      </div>
      <div v-if="isAuthorized('yautyadmin')" data-bs-trigger="hover" :ref="el => { listItems[4] = el }">
        <li class="nav-item" @click="animateSlider(5)">
          <router-link to="/yfs" class="route-linker" :class="router.currentRoute.value.name === 'yfs' ? 'active' : ''
            ">
            <div class="sidenav-item">
              <div class="sidenav-link yfs">
                <div class="item-icon">
                  <img class="navIcons" :src="adminIcon" style="width:3.2dvw;" />
                </div>
                <div class="menu-label">yfs</div>
              </div>
            </div>
          </router-link>
        </li>
      </div>
    </ul>

    <div class="navbar__toggle-tablet">
      <font-awesome-icon icon="chevron-down" class="chevron-up" v-show="isMenuOpen" @click="toggleMenu" />
      <font-awesome-icon icon="chevron-down" class="chevron-down" v-show="!isMenuOpen" @click="toggleMenu" />
    </div>
    <ul :class="{ 'navbar__menu': true, 'navbar__menu--open': isMenuOpen }">
      <div v-if="isMenuOpen" class="vertical-tablet">
        <div class="global-searcher-wrapper">
          <GlobalSearcher />
        </div>
        <div class="menus d-flex">
          <div class="d-flex notifications-item">
            <Notifications />
          </div>
          <div class="user-dropdown desktop" id="userDropdown">
            <MazDropdown trigger="click" size="xs" position="bottom right" no-chevron>
              <template #default>
                <div class="user-icon-wrapper">
                  <div class="user-icon" :title="$t('user.user')">
                    {{ userNick }}
                  </div>
                </div>
              </template>
              <template #dropdown>
                <div class="user-dropdown-body">
                  <div class="title">
                    <div class="title-label">
                      {{ userName }}
                    </div>
                  </div>
                  <hr class="undername-separator" />
                  <div class="user-dropdown-actions">
                    <router-link to="/user" class="btn-header-route" :title="t('profileconfig')">
                      <div class="show-all-wrapper">
                        <span>{{ $t("user.profile") }}</span>
                      </div>
                    </router-link>
                    <router-link to="/logout" class="btn-header-route" :title="t('logout.logout')">
                      <div class="show-all-wrapper">
                        <span>{{ $t("logout.logout") }}</span>
                      </div>
                    </router-link>
                  </div>
                </div>
              </template>
            </MazDropdown>
          </div>
        </div>
      </div>
    </ul>
  </nav>
</template>

<style lang="scss" scoped>
.global-searcher-wrapper {
  margin-left: 20px;
  flex-grow: 1;
}

.top-navbar-vertical-tablet {
  display: none;
}

.vertical-tablet {
  display: none;
}

.navbar {
  * {
    font-family: 'CircularStd-Book';
  }

  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #515151;
  border-bottom: 1px solid #f5f5f5;
  height: 61px;
  position: relative;

  &__toggle-tablet {
    display: none;
  }

  &__toggle {
    display: none;
    font-size: 1.5rem;
    cursor: pointer;
    z-index: 6;
  }

  &__menu {
    overflow: hidden;
    display: none;
    list-style: none;
    display: flex;
    margin: 0;
    padding: 0;

    @media (max-width: 576px) {
      max-height: 0;
      overflow: hidden;
      flex-direction: column;
      align-items: flex-start;
      width: 100%;
      transition: max-height 0.3s ease-in-out;
      margin-top: 52px;
      position: absolute;
      right: 0;
      border-radius: 0px 0px 12px 12px;

      &--open {
        z-index: 6;
        max-height: 100dvh;
        margin-top: 52px;
        border-radius: 0px 0px 12px 12px;
      }
    }
  }

  &__item {
    margin-left: 1rem;
  }

  .search {
    margin-left: 16px;
  }

  a {
    display: block;
    width: 100%;
    color: #515151;
    cursor: pointer;
    text-decoration: none;
    font-weight: 600;

    &:nth-child(3) {
      border-bottom-left-radius: calc(0.375rem - 1px);
      border-bottom-right-radius: calc(0.375rem - 1px);
    }

    &:nth-child(1) {
      border-top-left-radius: calc(0.375rem - 1px);
      border-top-right-radius: calc(0.375rem - 1px);
    }

    &:hover {
      color: #0195d8;
    }

    img {
      width: 100%;
      height: 100%;
    }
  }

  .yauty-logo {
    padding-left: 25px;
    margin-top: 10px;
    visibility: hidden;

    @media (max-width: 576px) {
      visibility: visible;
    }
  }

  .dropdown-menu[data-bs-popper] {
    left: -5.7rem;
    top: 5dvh;
  }

  .dropdown-menu {
    --bs-dropdown-zindex: 1000;
    --bs-dropdown-min-width: 10rem;
    --bs-dropdown-padding-x: 0;
    --bs-dropdown-padding-y: 0rem;
    --bs-dropdown-spacer: 0.125rem;
    --bs-dropdown-font-size: 1rem;
    --bs-dropdown-color: #212529;
    --bs-dropdown-bg: #fff;
    --bs-dropdown-border-color: var(--bs-border-color-translucent);
    --bs-dropdown-border-radius: 0.375rem;
    --bs-dropdown-border-width: 1px;
    --bs-dropdown-inner-border-radius: calc(0.375rem - 1px);
    --bs-dropdown-divider-bg: var(--bs-border-color-translucent);
    --bs-dropdown-divider-margin-y: 0rem;
    --bs-dropdown-box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    --bs-dropdown-link-color: #212529;
    --bs-dropdown-link-hover-color: #1e2125;
    --bs-dropdown-link-hover-bg: #e9ecef;
    --bs-dropdown-link-active-color: #fff;
    --bs-dropdown-link-active-bg: #0d6efd;
    --bs-dropdown-link-disabled-color: #adb5bd;
    --bs-dropdown-item-padding-x: 0.9rem;
    --bs-dropdown-item-padding-y: 0.9rem;
    --bs-dropdown-header-color: #6c757d;
    --bs-dropdown-header-padding-x: 1rem;
    --bs-dropdown-header-padding-y: 1rem;
    position: absolute;
    z-index: var(--bs-dropdown-zindex);
    min-width: var(--bs-dropdown-min-width);
    padding: var(--bs-dropdown-padding-y) var(--bs-dropdown-padding-x);
    margin: 0;
    font-size: var(--bs-dropdown-font-size);
    color: var(--bs-dropdown-color);
    text-align: left;
    list-style: none;
    background-color: var(--bs-dropdown-bg);
    background-clip: padding-box;
    border: var(--bs-dropdown-border-width) solid var(--bs-dropdown-border-color);
    border-radius: var(--bs-dropdown-border-radius);
  }

  .branding__link {
    color: white;
    background-color: v-bind(userColor);
    height: 32px;
    width: 32px;
    text-align: center;
    border-radius: 30px;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.route-linker {
  &.active {
    .item-icon {
      padding-left: 10px;
      margin-left: 20px;
    }

    .sidenav-link {
      margin-left: 0px;
      background-color: v-bind(selectedColor);
    }
  }
}

.sidenav-link {
  display: flex;
  width: 100dvw;
  gap: 22px;
  margin-left: 30px;
  padding-left: 10px;
  padding-bottom: 20px;
  padding-top: 20px;
  position: relative;
  color: white;
  font-size: 16px;
  cursor: pointer;
}

.sidenav-item {
  border-bottom: 0.1px solid rgba(194, 194, 194, 0.3);
  margin-left: -15px;
}

.selected .sidenav-item {
  background-color: v-bind(selectedColor);
}

.menu-label {
  padding: 5px 0px;
}

.menu-label.car {
  margin-left: -3px;
}

.menu-label.yfs {
  margin-left: 5px;
}

.menu-label.exit {
  margin-left: 2.5px;
}

.menu {
  position: absolute;
  top: 10px;
  right: 0;
  display: flex;
  flex: 3;
  gap: 20px;
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 0;
  padding-right: 1.2dvw;

  @media (max-width:576px) {
    display: none;
  }

  .nav-item {
    padding: 0 20px;
  }
}

.user-dropdown-body {
  display: flex;
  flex-direction: column;
  padding: 1rem 1rem;
  min-height: 100px;
  min-width: 200px;

  .title {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .title-label {
      font: normal normal normal 26px/19px 'CircularStd-Bold';
      letter-spacing: 0px;
      color: #153440;
      margin-bottom: 1rem;
    }
  }

  .actions {
    display: flex;
    justify-content: flex-end;
    font-size: 11px;

    .mark-all-read {
      margin-bottom: 0.5rem;
    }

    .mark-all-read:hover {
      cursor: pointer;
      text-decoration: underline;
    }

    .light-grey {
      color: #cbd5e1;
    }
  }

  .notifications-list {
    flex-grow: 1;
    margin-top: 1rem;
  }

  .user-dropdown-actions {
    display: flex;
    flex-direction: column;
    margin-top: 1rem;
    justify-content: center;
    align-items: center;
    gap: 1.5rem;

    a {
      text-decoration: none;
      color: #153440;
      font: normal normal normal 16px/19px 'CircularStd-Bold';
    }
  }

  .undername-separator {
    margin: 0;
    margin-top: -5px;
    margin-bottom: 5px;
    color: #a8b0ba;
  }
}

.user-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: v-bind(userColor);
  color: white;
  font: normal normal normal 18px/16px 'CircularStd-Bold';

}

//tablets verticales
@media (min-width: 577px) and (max-width: 1024px) and (orientation: portrait) {
  .selected .sidenav-item {
    background-color: transparent;
  }

  .top-navbar-vertical-tablet {
    position: absolute;
    display: flex;
    flex-direction: row;
    width: 100dvw;
    justify-content: flex-end;
    align-items: center;
    margin-bottom: 0px;

    .hSlider {
      width: 6.8dvw !important;
      display: flex;
      flex-direction: row;
      justify-content: center;
      height: 3px;
      border-radius: 3px;
      background-color: v-bind(userSecondaryColor);
      position: relative;
      z-index: 1;
      top: 3dvh !important;
      left: 8dvw;
      transition: transform 0.3s ease-in-out;
    }

    li {
      list-style: none;
    }

    .sidenav-item {
      border-bottom: none;
      margin-left: 0px;
      padding-left: 0px;
      height: 8dvh;
      width: auto;

      .sidenav-link {
        background-color: transparent;
        padding: 0px !important;
        margin-left: 0px;
        margin-right: 0px;
        width: 9.5dvw;
        height: 100%;
        gap: 0px !important;
        justify-content: center;

        .menu-label {
          display: none;
        }
      }
    }

    .route-linker {
      padding: 0px;

      &.active {
        background-color: v-bind(selectedColor);

        .item-icon {
          border-left: none;
          margin-left: 0px;
          padding-left: 0px;
          padding-right: 0px;
        }
      }
    }
  }

  .vertical-tablet {
    display: flex;
    width: 100dvw;
    align-items: center;
    padding-right: 25px !important;
    height: 8dvh;

    li {
      list-style: none;
    }
  }

  .phone-item {
    display: none;
  }

  ul.navbar__menu {
    overflow: visible;
    position: relative;
  }

  .navbar {
    padding: 0px;
    border-bottom: none;
    background: transparent linear-gradient(270deg, v-bind(userColor) 0%, v-bind(userTertiaryColor) 100%) 0% 0% no-repeat padding-box;
    height: 8dvh;
    position: fixed;
    width: 100dvw;
    transition: transform 0.18s ease-in-out;
    z-index: 1000;
    overflow: visible;

    &.hidden {
      transform: translateY(-100%);
    }

    &__toggle {
      display: none;
    }

    &__toggle-tablet {
      display: block;
      position: absolute;
      z-index: 1000;
      max-width: 30px;
      margin-left: 16dvw;
      margin-top: 1.5dvh;

      .chevron-down {
        font-size: 16px;
        color: white;
      }

      .chevron-up {
        font-size: 16px;
        color: white;
        transform: rotate(180deg);
      }

      .icon {
        height: 1.2em;
        margin-left: 100px;
        color: white;
      }
    }

    .icon-user {
      height: 1.7em !important;
      color: white;
    }

    &__menu {
      max-height: 0;
      overflow: hidden;
      flex-direction: column;
      align-items: flex-start;
      width: 100%;
      transition: max-height 0.3s ease-in-out;
      background-color: white;
      background-image: none;
      margin-top: 50dvh;
      position: absolute;
      top: -48.5dvh;
      right: 0;
      border-radius: 0px 0px 0px 0px;

      &--open {
        z-index: 6;
        max-height: 800px;
        margin-top: 50.7dvh;
        border-radius: 0px 0px 0px 0px;
      }

    }
  }

  .menu {
    display: none;
  }

  .yauty-logo {
    margin-top: 2.9dvh !important;
    visibility: visible !important;
    z-index: 999;

    img {
      width: 12dvw !important;
    }
  }

  .navbar__item.d-flex.notifications-item {
    position: relative;
    z-index: 1001;
  }
}

@media (min-width:577px) and (max-width: 686px) {

  .top-navbar-vertical-tablet {
    position: absolute;
    display: flex;
    flex-direction: row;
    width: 100dvw;
    justify-content: flex-end;
    align-items: center;
    margin-bottom: 0px;

    .hSlider {
      width: 6.4dvw !important;
      display: flex;
      flex-direction: row;
      justify-content: center;
      height: 3px;
      border-radius: 3px;
      background-color: v-bind(userSecondaryColor);
      position: relative;
      z-index: 1;
      top: 3dvh !important;
      left: 7.8dvw;
      transition: transform 0.3s ease-in-out;
    }
  }

  .navbar {
    &__toggle-tablet {
      display: block;
      position: absolute;
      z-index: 1000;
      max-width: 30px;
      margin-left: 17dvw;
      margin-top: 1.5dvh;

      .chevron-down {
        font-size: 16px;
        color: white;
      }

      .chevron-up {
        font-size: 16px;
        color: white;
        transform: rotate(180deg);
      }

      .icon {
        height: 1.2em;
        margin-left: 100px;
        color: white;
      }
    }
  }
}

@media (max-width: 1024px) and (orientation: landscape) {
  .slidebar {
    display: none !important;
  }

  .selected .sidenav-item {
    background-color: transparent;
  }

  .top-navbar-vertical-tablet {
    position: absolute;
    display: flex;
    flex-direction: row;
    width: 100dvw;
    justify-content: flex-end;
    align-items: center;
    margin-bottom: 0px;

    .hSlider {
      width: 6.8dvw !important;
      display: flex;
      flex-direction: row;
      justify-content: center;
      height: 3px;
      border-radius: 3px;
      background-color: v-bind(userSecondaryColor);
      position: relative;
      z-index: 1;
      top: 25px !important;
      left: 7.2dvw;
      transition: transform 0.3s ease-in-out;
      @media (min-width: 577px){
        top: 4dvh !important;
      }
    }

    li {
      list-style: none;
    }

    .sidenav-item {
      border-bottom: none;
      margin-left: 0px;
      padding-left: 0px;
      height: 65px;
      width: auto;
      @media (min-width: 577px){
        height: 10dvh;
      }

      .sidenav-link {
        background-color: transparent;
        padding: 0px !important;
        margin-left: 0px;
        margin-right: 0px;
        width: 7.5dvw;
        height: 85%;
        gap: 0px !important;
        justify-content: center;
        top: 5px;
        @media (min-width: 577px){
          height: 10dvh;
        }

        .menu-label {
          display: none;
        }
      }
    }

    .route-linker {
      padding: 0px;

      &.active {
        background-color: v-bind(selectedColor);

        .item-icon {
          border-left: none;
          margin-left: 0px;
          padding-left: 0px;
          padding-right: 0px;
        }
      }
    }
  }

  .vertical-tablet {
    display: flex;
    width: 100dvw;
    align-items: center;
    padding-right: 25px !important;
    height: 65px;

    li {
      list-style: none;
    }
  }

  .phone-item {
    display: none;
  }

  ul.navbar__menu {
    overflow: visible;
    position: relative;
  }

  .navbar {
    padding: 0px;
    border-bottom: none;
    background: transparent linear-gradient(270deg, v-bind(userColor) 0%, v-bind(userTertiaryColor) 100%) 0% 0% no-repeat padding-box;
    height: 65px;
    min-height: 65px;
    position: fixed;
    width: 100dvw;
    transition: transform 0.18s ease-in-out;
    z-index: 1000;
    overflow: visible;
    @media (max-width: 576px){
      height: 15dvh;
    }

    &.hidden {
      transform: translateY(-100%);
    }

    &__toggle {
      display: none;
    }

    &__toggle-tablet {
      display: block;
      position: absolute;
      z-index: 1000;
      max-width: 30px;
      margin-left: 16dvw;

      .chevron-down {
        font-size: 16px;
        color: white;
      }

      .chevron-up {
        font-size: 16px;
        color: white;
        transform: rotate(180deg);
      }

      .icon {
        height: 1.2em;
        margin-left: 100px;
        color: white;
      }
    }

    .icon-user {
      height: 1.7em !important;
      color: white;
    }

    &__menu {
      max-height: 0;
      overflow: hidden;
      flex-direction: column;
      align-items: flex-start;
      width: 100%;
      transition: max-height 0.3s ease-in-out;
      background-color: white;
      background-image: none;
      margin-top: 15px;
      position: absolute;
      right: 0;
      border-radius: 0px 0px 0px 0px;

      &--open {
        z-index: 6;
        max-height: 800px;
        margin-top: 3px;
        border-radius: 0px 0px 0px 0px;
      }

      @media (max-width: 950px) and (orientation: landscape) {
        margin-top: 16px;
          
      }

    }
  }

  .menu {
    display: none;
  }

  .yauty-logo {
    margin-top: 2.9dvh !important;
    visibility: visible !important;
    z-index: 999;

    img {
      width: 12dvw !important;
    }
  }

  .navbar__item.d-flex.notifications-item {
    position: relative;
    z-index: 1001;
  }

  .nav-item {
    bottom: 5px;
  }
}
</style>
